import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import './TournamentList.css';

/** Avoid black / near-black panel backgrounds from theme `box_color`. */
const TL_LIGHT_SURFACE = 'rgba(255, 255, 255, 0.94)';

/** Optional `tournamentId` values to omit from the list (strings). */
const TL_EXCLUDED_TOURNAMENT_IDS = new Set(
  [
    /* e.g. '43646', */
  ]
    .filter(Boolean)
    .concat(
      String(process.env.REACT_APP_TOURNAMENT_LIST_EXCLUDE_IDS || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    )
);

function sanitizeTournamentList(raw) {
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  const out = [];
  for (const t of raw) {
    if (!t) continue;
    const id = t.tournamentId;
    if (id == null) continue;
    if (TL_EXCLUDED_TOURNAMENT_IDS.has(String(id))) continue;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(t);
  }
  return out;
}

function parseRgbFromCssColor(cssColor) {
  if (!cssColor || typeof cssColor !== 'string') return null;
  const c = cssColor.trim().toLowerCase();
  if (c === 'black' || c === '#000' || c === '#000000') return { r: 0, g: 0, b: 0 };
  const hex = c.match(/^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i);
  if (hex) {
    let h = hex[1];
    if (h.length === 3) h = h.split('').map((x) => x + x).join('');
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return { r, g, b };
  }
  const rgb = c.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (rgb) return { r: +rgb[1], g: +rgb[2], b: +rgb[3] };
  return null;
}

function isSurfaceTooDark(cssColor) {
  const rgb = parseRgbFromCssColor(cssColor);
  if (!rgb) return false;
  const lum = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return lum < 0.28;
}

function surfaceBackgroundFromTheme(boxcolor) {
  if (boxcolor == null || String(boxcolor).trim() === '') return null;
  const t = String(boxcolor).trim();
  if (isSurfaceTooDark(t)) return TL_LIGHT_SURFACE;
  return t;
}

function textLuminance(cssColor) {
  const rgb = parseRgbFromCssColor(cssColor);
  if (!rgb) return null;
  return (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
}

const TL_SWAL_BTN_DEFAULT = '#1548a0';
const TL_SWAL_BTN_TEXT_DEFAULT = '#ffffff';

const TL_SWAL_SUCCESS_ICON_HTML = [
  '<svg class="tl-swal-check-svg" viewBox="0 0 52 52" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">',
  '<circle class="tl-swal-check-circle" cx="26" cy="26" r="24" fill="none" />',
  '<path class="tl-swal-check-path" fill="none" d="M14.5 27.5l7.5 7.5 16.5-17" />',
  '</svg>'
].join('');

/** SweetAlert2 tuned to match the tournament list UI. */
function fireTournamentListAlert({
  variant,
  title,
  text,
  confirmButtonText = 'OK',
  buttonBg,
  buttonText
}) {
  const success = variant === 'success';
  const bg = buttonBg || (success ? TL_SWAL_BTN_DEFAULT : '#dc2626');
  const fg = buttonText || TL_SWAL_BTN_TEXT_DEFAULT;

  return Swal.fire({
    icon: success ? 'success' : 'error',
    ...(success
      ? { iconHtml: TL_SWAL_SUCCESS_ICON_HTML }
      : { iconColor: '#f87171' }),
    title,
    text,
    confirmButtonText,
    buttonsStyling: false,
    width: '26.5rem',
    padding: 0,
    backdrop: 'rgba(15, 23, 42, 0.55)',
    allowOutsideClick: true,
    showCloseButton: false,
    customClass: {
      popup: `tl-swal-popup${success ? ' tl-swal-popup--success' : ' tl-swal-popup--error'}`,
      title: 'tl-swal-title',
      htmlContainer: 'tl-swal-html',
      confirmButton: 'tl-swal-btn',
      actions: 'tl-swal-actions',
      icon: success ? 'tl-swal-icon-wrap tl-swal-success-icon' : 'tl-swal-icon-wrap'
    },
    didOpen: (popup) => {
      const btn = popup.querySelector('.tl-swal-btn');
      if (btn) {
        btn.style.backgroundColor = bg;
        btn.style.color = fg;
        btn.style.border = 'none';
      }
    }
  });
}

const TournamentList = ({ updateHeader }) => {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [themeData, setThemeData] = useState(null);
  const navigate = useNavigate();
  const hasCalledApi = useRef(false);
  const intervalRef = useRef(null);
  const [textcolor, Setcolorcode] = useState(null);
  const [boxcolor, SetBoxcolorcode] = useState(null);
  const [boxtextcolor, SetBoxTextcolor] = useState(null);
  const [Buttoncolor, SetButtoncolor] = useState(null);
  const [buttontextcolor, SetButtonTextcolor] = useState(null);
  const [, SetriddlerusLogo] = useState('/img/logo.png');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (themeData != null) {
      const body = document.body;

      if (body.classList.contains('common-bg')) {
        if (window.innerWidth <= 768) {
          body.style.backgroundImage = `url(${themeData.background_mob})`;
        } else {
          body.style.backgroundImage = `url(${themeData.background_desk})`;
        }
      }
      SetriddlerusLogo(themeData.logo);
    }
  }, [themeData]);

  useEffect(() => {
    updateHeader({
      backButtonRedirect: '/userpage'
    });

    const checkSessionData = () => {
      const storedSession = sessionStorage.getItem('userData');
      const storedTheme = sessionStorage.getItem('themeData');

      if (storedSession && storedTheme) {
        const parsedTheme = JSON.parse(storedTheme);

        setThemeData(parsedTheme);
        Setcolorcode(parsedTheme.landing_page_title_color);
        SetBoxcolorcode(parsedTheme.box_color);
        SetBoxTextcolor(parsedTheme.box_text_color);
        SetButtonTextcolor(parsedTheme.button_text_color);
        SetButtoncolor(parsedTheme.button_color);
        return true;
      }
      navigate('/login');
      return false;
    };

    if (!hasCalledApi.current) {
      const sessionExists = checkSessionData();
      if (sessionExists) {
        hasCalledApi.current = true;
        fetchTournaments(true);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [navigate, updateHeader]);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      fetchTournaments(false);
    }, 3000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const fetchTournaments = async (showLoading = false) => {
    try {
      if (showLoading) {
        setLoading(true);
      }

      const storedSession = sessionStorage.getItem('userData');
      const storedTheme = sessionStorage.getItem('themeData');

      if (!storedSession || !storedTheme) {
        if (showLoading) {
          setLoading(false);
        }
        return;
      }

      const parsedUser = JSON.parse(storedSession);
      const parsedTheme = JSON.parse(storedTheme);

      const response = await axios.post(
        `${process.env.REACT_APP_PHP_ADMIN_BASE_URL}getTournamentList.php`,
        {
          sessionId: parsedUser.sessionId,
          userId: parsedUser.userId,
          showIntercorporate: true
        },
        {
          headers: {
            'Content-Type': 'text/plain'
          }
        }
      );

      if (response.data && response.data.tournaments) {
        const list = sanitizeTournamentList(response.data.tournaments);
        setTournaments(list);

        const sessionAgain = sessionStorage.getItem('userData');
        if (sessionAgain) {
          const parsed = JSON.parse(sessionAgain);
          const joinedTournaments = list.filter((t) => t.alreadyJoined);

          if (joinedTournaments.length > 0) {
            parsed.tournamentId = joinedTournaments[0].tournamentId;
            sessionStorage.setItem('userData', JSON.stringify(parsed));
          }
        }
      }
    } catch (error) {
      console.error('Error fetching tournaments:', error);
      fireTournamentListAlert({
        variant: 'error',
        title: 'Couldn’t load tournaments',
        text: 'Please check your connection and try again.',
        confirmButtonText: 'OK'
      });
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const joinTournament = async (tournamentId) => {
    try {
      const storedSession = sessionStorage.getItem('userData');
      const storedTheme = sessionStorage.getItem('themeData');

      if (!storedSession || !storedTheme) {
        fireTournamentListAlert({
          variant: 'error',
          title: 'Session expired',
          text: 'Please sign in again to register.',
          confirmButtonText: 'OK'
        });
        return;
      }

      const parsedUser = JSON.parse(storedSession);
      const dataContents = {
        tournamentId,
        sessionId: parsedUser.sessionId,
        userId: parsedUser.userId,
        roles: parsedUser.source,
        email: parsedUser.email,
        name: parsedUser.name
      };

      const apiUrl = `${process.env.REACT_APP_PHP_ADMIN_BASE_URL}joinTournament.php`;

      const response = await axios.post(apiUrl, dataContents, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = response.data;
      if (data.response && data.response.success) {
        const sessionAgain = sessionStorage.getItem('userData');
        if (sessionAgain) {
          const parsed = JSON.parse(sessionAgain);
          parsed.tournamentId = tournamentId;
          sessionStorage.setItem('userData', JSON.stringify(parsed));
        }

        fireTournamentListAlert({
          variant: 'success',
          title: 'You’re registered!',
          text: "You’ve successfully joined this tournament. We’ll refresh the list in a moment.",
          confirmButtonText: 'Great',
          buttonBg: Buttoncolor || TL_SWAL_BTN_DEFAULT,
          buttonText: buttontextcolor || TL_SWAL_BTN_TEXT_DEFAULT
        });
        setTimeout(() => {
          fetchTournaments(false);
        }, 1000);
      } else {
        fireTournamentListAlert({
          variant: 'error',
          title: 'Couldn’t register',
          text: data.response?.error || 'Something went wrong. Please try again.',
          confirmButtonText: 'OK'
        });
      }
    } catch (error) {
      console.error('Error joining tournament:', error);
      fireTournamentListAlert({
        variant: 'error',
        title: 'Couldn’t register',
        text: 'A network error occurred. Please try again.',
        confirmButtonText: 'OK'
      });
    }
  };

  const viewTournament = (tournamentId) => {
    navigate(`/tournamentStructure?tournamentId=${tournamentId}`);
  };

  const renderButton = (tournament) => {
    const { generated, alreadyJoined, tournamentId, deadlineDate, checkJoinedallCount, participation_limit } = tournament;
    const isDeadlinePassed = new Date(deadlineDate) < new Date();
    const isUserLimitExceeded = checkJoinedallCount >= participation_limit;

    const btnClass = 'tl-btn tl-btn--table';

    if (!alreadyJoined && isUserLimitExceeded) {
      return (
        <button type="button" className={`${btnClass} tl-btn--muted`} disabled>
          Limit full
        </button>
      );
    }

    if (generated) {
      return (
        <button type="button" className={btnClass} onClick={() => viewTournament(tournamentId)}>
          Open
        </button>
      );
    }

    if (!alreadyJoined && isDeadlinePassed) {
      return (
        <button type="button" className={`${btnClass} tl-btn--muted`} disabled>
          Closed
        </button>
      );
    }

    if (alreadyJoined && !generated) {
      return (
        <button type="button" className={`${btnClass} tl-btn--muted`} disabled>
          Registered
        </button>
      );
    }

    return (
      <button type="button" className={btnClass} onClick={() => joinTournament(tournamentId)}>
        Register
      </button>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const filteredTournaments = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return tournaments;
    return tournaments.filter((t) => (t.name || '').toLowerCase().includes(q));
  }, [tournaments, searchQuery]);

  const totalPages = Math.ceil(filteredTournaments.length / itemsPerPage) || 1;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentTournaments = filteredTournaments.slice(indexOfFirstItem, indexOfLastItem);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [filteredTournaments.length, currentPage, totalPages]);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const themeStyle = useMemo(() => {
    const s = {};
    if (textcolor) s['--tl-title'] = textcolor;
    const safeCardBg = surfaceBackgroundFromTheme(boxcolor);
    if (safeCardBg) s['--tl-card-bg'] = safeCardBg;
    if (boxtextcolor) {
      const usedLightFallback =
        Boolean(boxcolor && String(boxcolor).trim()) &&
        safeCardBg === TL_LIGHT_SURFACE &&
        isSurfaceTooDark(String(boxcolor).trim());
      const tlum = textLuminance(boxtextcolor);
      if (usedLightFallback && tlum != null && tlum > 0.62) {
        /* Dark card + light text in theme; keep default dark body text on light surface. */
      } else {
        s['--tl-card-text'] = boxtextcolor;
      }
    }
    if (Buttoncolor) s['--tl-btn-bg'] = Buttoncolor;
    if (buttontextcolor) s['--tl-btn-text'] = buttontextcolor;
    return s;
  }, [textcolor, boxcolor, boxtextcolor, Buttoncolor, buttontextcolor]);

  const textColorStyle = textcolor ? { color: textcolor } : undefined;

  if (loading) {
    return (
      <div className="tl-page" style={themeStyle}>
        <div className="tl-loading">
          <div className="tl-loading__spinner" aria-hidden />
          <span style={textColorStyle}>Loading tournaments…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="tl-page" style={themeStyle}>
      <header className="tl-header">
        <h1 className="tl-title" style={textColorStyle}>
          Tournaments
        </h1>
        <p className="tl-subtitle" style={textColorStyle}>
          Register before the deadline or open your bracket when it’s ready.
        </p>
      </header>

      <div className="tl-toolbar">
  
        <div className="tl-search-wrap" style={textColorStyle}>
          <svg className="tl-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            type="search"
            className="tl-search"
            placeholder="Search by tournament name…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search tournaments"
            style={textColorStyle}
          />
        </div>
      </div>

      <div className="tl-table-panel">
        {tournaments.length === 0 ? (
          <div className="tl-empty tl-empty--in-panel" role="status" style={textColorStyle}>
            <strong style={textColorStyle}>No tournaments right now</strong>
            Check back later or contact your organizer.
          </div>
        ) : filteredTournaments.length === 0 ? (
          <div className="tl-empty tl-empty--in-panel" role="status" style={textColorStyle}>
            <strong style={textColorStyle}>No matches</strong>
            Try a different search term.
          </div>
        ) : (
          <div className="tl-table-scroll">
            <table className="tl-table">
              <thead>
                <tr>
                  <th scope="col">Tournament</th>
                  <th scope="col">Status</th>
                  <th scope="col">Starts</th>
                  <th scope="col">Register by</th>
                  <th scope="col" className="tl-table__th-action">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentTournaments.map((tournament, i) => {
                  const globalIdx = indexOfFirstItem + i;
                  const {
                    generated,
                    alreadyJoined,
                    tournamentId,
                    name,
                    startDate,
                    deadlineDate,
                    checkJoinedallCount,
                    participation_limit
                  } = tournament;
                  const isDeadlinePassed = new Date(deadlineDate) < new Date();
                  const isUserLimitExceeded = checkJoinedallCount >= participation_limit;

                  let statusBadges = null;
                  if (alreadyJoined && generated) {
                    statusBadges = (
                      <>
                        <span className="tl-badge tl-badge--joined">In</span>
                        <span className="tl-badge tl-badge--live">Live</span>
                      </>
                    );
                  } else if (alreadyJoined && !generated) {
                    statusBadges = (
                      <>
                        <span className="tl-badge tl-badge--joined">In</span>
                        <span className="tl-badge tl-badge--wait">Pending</span>
                      </>
                    );
                  } else if (!alreadyJoined && isUserLimitExceeded) {
                    statusBadges = <span className="tl-badge tl-badge--full">Full</span>;
                  } else if (!alreadyJoined && isDeadlinePassed) {
                    statusBadges = <span className="tl-badge tl-badge--closed">Closed</span>;
                  } else {
                    statusBadges = <span className="tl-badge tl-badge--open">Open</span>;
                  }

                  return (
                    <tr key={tournamentId || globalIdx}>
                      <td className="tl-table__name" data-label="Tournament" style={textColorStyle}>
                        {name || 'Tournament'}
                      </td>
                      <td className="tl-table__status" data-label="Status" style={textColorStyle}>
                        <span className="tl-table__badges">{statusBadges}</span>
                      </td>
                      <td className="tl-table__date" data-label="Starts" style={textColorStyle}>
                        <span className="tl-table__date-line">{formatDate(startDate)}</span>
                        <span className="tl-table__time">{formatTime(startDate)}</span>
                      </td>
                      <td className="tl-table__date" data-label="Register by" style={textColorStyle}>
                        <span className="tl-table__date-line">{formatDate(deadlineDate)}</span>
                        <span className="tl-table__time">{formatTime(deadlineDate)}</span>
                      </td>
                      <td className="tl-table__action" data-label="Action" style={textColorStyle}>
                        {renderButton(tournament)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {filteredTournaments.length > itemsPerPage && (
        <nav className="tl-pagination" aria-label="Tournament pages">
          <button
            type="button"
            className="tl-pagination__btn"
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage <= 1}
          >
            Previous
          </button>
          <span className="tl-pagination__info" style={textColorStyle}>
            Page {currentPage} of {totalPages}
          </span>
          <button
            type="button"
            className="tl-pagination__btn"
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage >= totalPages}
          >
            Next
          </button>
        </nav>
      )}
    </div>
  );
};

export default TournamentList;
